/* eslint-disable */
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { LocalizationContext } from "../../App";
import { cn } from "../../common/helpers/formatters";
import { CheckCircle, InfoCircle } from "../../common/icons";
import { OrderOfBeneficiariesModelType } from "../../language/localization";
import { PolicyBackOfficeDetails } from "../../models/PolicyBackOfficeDetails";
import { UpdatePolicyBackOfficeDetailsRequest } from "../../models/request/UpdatePolicyBackOfficeDetailsRequest";
import {
  useGetBackOfficeDetailsQuery,
  useUpdateBackOfficeDetailsMutation,
} from "../../store/policiesSlice";

type OrderOfBeneficiariesModelConfig = {
  id: 1 | 2;
  listItems: string[];
  name: string;
  type: OrderOfBeneficiariesModelType;
};

export const OrderOfBeneficiaries = () => {
  const navigate = useNavigate();
  const { state = {} } = useLocation();
  const { policyUId, productVersionCode } = state as {
    policyUId: string;
    productVersionCode: string;
  };
  const l10n = useContext(LocalizationContext);
  const models: Record<OrderOfBeneficiariesModelType, OrderOfBeneficiariesModelConfig> = {
    partnership: {
      id: 1,
      listItems: l10n.benefModels.partnership.order,
      name: l10n.benefModels.partnership.name,
      type: "partnership",
    },
    alternative: {
      id: 2,
      listItems: l10n.benefModels.alternative.order,
      name: l10n.benefModels.alternative.name,
      type: "alternative",
    },
  };
  const [selectedModel, selectModel] = useState<OrderOfBeneficiariesModelType | undefined>(
    undefined
  );
  const { data, isLoading: isFetching } = useGetBackOfficeDetailsQuery(policyUId);
  const [
    updatePolicyBackOfficeDetails,
    { isError: isUpdateFailed, isLoading: isUpdating, isSuccess: isUpdateSuccess, isUninitialized },
  ] = useUpdateBackOfficeDetailsMutation();
  const [notificationType, setNotificationType] = useState<"success" | "danger" | undefined>(
    undefined
  );

  useEffect(() => {
    const listOfBeneficiaries = (data?.backOfficeDetails as PolicyBackOfficeDetails)
      ?.listOfBeneficiaries;
    if (listOfBeneficiaries) {
      const modelType = Object.values(models).find((m) => m.id === listOfBeneficiaries)?.type;
      if (modelType) {
        selectModel(modelType);
      }
    }
  }, [data]);

  const updateBackOfficeDetails = () => {
    if (selectedModel) {
      const request: UpdatePolicyBackOfficeDetailsRequest = {
        policyIdentifier: policyUId,
        productVersionCode: productVersionCode,
        backOfficeDetails: {
          beneficiaries: [],
          listOfBeneficiaries: models[selectedModel].id,
        },
      };

      updatePolicyBackOfficeDetails(request);
    }
  };

  useEffect(() => {
    if (!isUninitialized && !isUpdating) {
      if (isUpdateSuccess) {
        setNotificationType("success");
      } else if (isUpdateFailed) {
        setNotificationType("danger");
      }

      setTimeout(() => {
        setNotificationType(undefined);
      }, 3000);
    }
  }, [isUpdateFailed, isUpdateSuccess, isUpdating, isUninitialized]);

  if (!policyUId || !productVersionCode) navigate("/");

  if (isFetching)
    return (
      <div className="loading-screen flex flex-col justify-center items-center">
        <div className="text-body text-xl">{l10n.common.loading}</div>
      </div>
    );

  return (
    <>
      <h2>{l10n.title.orderOfBeneficiaries}</h2>
      <p>{l10n.notice.chooseOrderOfBeneficiariesForDeathInsurance}</p>
      <form>
        {Object.values(models).map((model) => (
          <div
            className={cn("card-option", selectedModel === model.type && "card-option--selected")}
            key={model.id}
            onClick={() => selectModel(model.type)}
          >
            <div className="card-header gap-4 items-start pb-0 relative">
              <h2 className="!mb-0 text-card-option-title font-card-option-title">{model.name}</h2>
              <div className="form-check">
                <input
                  aria-label={model.type}
                  checked={selectedModel === model.type}
                  className="form-check-input bg-transparent"
                  id={model.type}
                  name={model.type}
                  onChange={() => {}}
                  type="radio"
                />
              </div>
            </div>
            <div className="card-body text-secondary">
              <div className="flex flex-col gap-3">
                <ol className="list-decimal list-inside">
                  {model.listItems.map((item, index) => (
                    <li className="text-card" key={`${model.name}-item-${index}`}>
                      {item}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        ))}

        {notificationType !== undefined && (
          <div className={`notification notification-${notificationType} mb-3`}>
            {notificationType === "success" ? (
              <CheckCircle className="w-[24px] h-[24px] shrink-0 stroke-success" />
            ) : (
              <InfoCircle className="w-[24px] h-[24px] shrink-0 fill-danger" />
            )}
            {notificationType === "success"
              ? l10n.notice.changeSuccessful
              : l10n.notice.somethingWentWrongPleaseTryAgain}
          </div>
        )}

        <button
          className="btn btn-primary"
          disabled={!selectedModel || isUpdating}
          onClick={(e) => {
            e.preventDefault();
            updateBackOfficeDetails();
          }}
        >
          {isUpdating ? l10n.common.loading : l10n.btn.confirm}
        </button>
      </form>
    </>
  );
};
